import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Store, Truck, Mail, Send, X, Loader, AlertTriangle, Users, CheckCircle2, Link2, History, BellRing } from "lucide-react";
import api from "../../../utils/api";
import { auth } from "../../../firebase";

// Who to contact about a shipment (Hasbro workspace): the customer, matched in the contact
// directory (Customer Accounts), and the carrier. "Request update" sends an email from the
// platform as "Yadata on behalf of <Hasbro sender>" (GET /api/hasbro/mail-sender); replies and a
// copy go to that sender. Every message is logged on the shipment. Backend: hasbroContactsController.

const inputDark = "w-full bg-[#0D1520] border border-[#1F2937] rounded-lg text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-500/60 px-3 py-2";
const fmtDateTime = v => (v ? new Date(v).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
const firstName = n => String(n || "").trim().split(/\s+/)[0] || "";
const myName = () => auth.currentUser?.displayName || (auth.currentUser?.email || "").split("@")[0];

/** Shipment facts sent with a message (and shown in the email) */
export function shipmentFacts(m) {
    const overdue = m.orders.filter(o => o.overdueDays > 0).length;
    const issue = m.departed?.severity === "issue" ? m.departed.reason
        : m.tnt?.severity === "issue" ? (m.tnt.reason || m.tnt.incident)
        : overdue ? `${overdue} order${overdue > 1 ? "s" : ""} past the ship-by date`
        : m.conflict ? "Shows as both still in dispatch and shipped" : null;
    return {
        key: String(m.orderReference || m.consignment || "").replace(/[^A-Za-z0-9:_-]/g, "").slice(0, 40),
        orderReference: m.orderReference || null, consignment: (m.departed || m.tnt)?.connote || m.consignment || null,
        po: m.po || null, customer: m.customer || null, carrier: m.carrier || null,
        status: [m.status, m.warehouseStage].filter(Boolean).join(" · ") || null,
        issue: issue || null,
    };
}

// Message templates — the text is filled in, then the user edits it before sending
const TEMPLATES = {
    request_update: {
        label: "Request a status update", for: "carrier",
        subject: s => `Status update request — consignment ${s.consignment || s.orderReference}`,
        body: (s, to, sig) => `Hi ${firstName(to?.name) || "team"},\n\nCould you please send us an update on the consignment below${s.issue ? ` — our latest report shows: ${s.issue}` : ""}?\n\nIn particular: where it is now, the booked delivery date / ETA, and anything you need from us.\n\nThank you,\n${sig}\nHasbro`,
    },
    report_issue: {
        label: "Report an issue", for: "carrier",
        subject: s => `Issue with consignment ${s.consignment || s.orderReference}`,
        body: (s, to, sig) => `Hi ${firstName(to?.name) || "team"},\n\nWe have an issue with the consignment below${s.issue ? `: ${s.issue}` : ""}.\n\nCould you please look into it and let us know the next steps and a revised delivery date?\n\nThank you,\n${sig}\nHasbro`,
    },
    customer_update: {
        label: "Update the customer", for: "customer",
        subject: s => `Update on your order${s.po ? ` — PO ${s.po}` : s.orderReference ? ` — shipment ${s.orderReference}` : ""}`,
        body: (s, to, sig) => `Hi ${to?.name || "there"},\n\nWe wanted to give you an update on your order below.${s.issue ? `\n\nCurrent status: ${s.issue}.` : ""}\n\nWe're working with the carrier and will let you know as soon as we have a confirmed delivery date.\n\nKind regards,\n${sig}\nHasbro`,
    },
    alert_colleague: {
        label: "Alert a colleague", for: "staff",
        subject: s => `Shipment needs attention — ${s.orderReference || s.consignment}`,
        body: (s, to, sig) => `Hi ${firstName(to?.name)},\n\nCan you take a look at the shipment below?${s.issue ? ` ${s.issue}.` : ""}\n\nThanks,\n${sig}`,
    },
};
const TEMPLATE_FOR = { carrier: "request_update", customer: "customer_update", staff: "alert_colleague" };

export default function ShipmentContacts({ m }) {
    const facts = useMemo(() => shipmentFacts(m), [m]);
    const [info, setInfo] = useState(null);           // lookup result | { error }
    const [messages, setMessages] = useState(null);
    const [dialog, setDialog] = useState(null);       // { type, id } preselected recipient
    const [linking, setLinking] = useState(false);

    const load = useCallback(async () => {
        try {
            const [{ data }, msgs] = await Promise.all([
                api.get("/api/hasbro/contacts/lookup", { params: { customer: m.customer || "", carrier: m.carrier || "" } }),
                facts.key ? api.get("/api/hasbro/shipment-messages", { params: { ref: facts.key } }) : Promise.resolve({ data: { messages: [] } }),
            ]);
            setInfo(data);
            setMessages(msgs.data.messages || []);
        } catch (e) {
            setInfo({ error: e.response?.data?.error || "Couldn't load contacts" });
        }
    }, [m.customer, m.carrier, facts.key]);
    useEffect(() => { load(); }, [load]);

    const confirmMatch = async contact => {
        setLinking(true);
        try { await api.post(`/api/hasbro/contacts/${contact.id}/aliases`, { name: m.customer }); await load(); }
        catch (e) { setInfo(i => ({ ...i, linkError: e.response?.data?.error || "Couldn't link the account" })); }
        finally { setLinking(false); }
    };

    if (!info) return <p className="text-xs text-slate-500 flex items-center gap-2"><Loader size={12} className="animate-spin" /> Loading contacts…</p>;
    if (info.error) return <p className="text-xs text-red-400">Contacts: {info.error}</p>;

    const cust = info.customer, carrier = info.carrier.contact;
    const hasIssue = !!facts.issue;
    const primary = carrier ? { type: "carrier", id: carrier.id } : cust.match?.email ? { type: "customer", id: cust.match.id } : { type: "carrier", id: info.carriers[0]?.id };

    return (
        <div>
            <div className="flex items-center justify-between gap-2 mb-1.5">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Contacts</p>
                <button onClick={() => setDialog(primary)}
                    className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold text-white ${hasIssue ? "bg-red-600 hover:bg-red-500" : "bg-blue-600 hover:bg-blue-500"}`}>
                    {hasIssue ? <BellRing size={13} /> : <Send size={13} />} {hasIssue ? "Request update" : "Contact"}
                </button>
            </div>
            <div className="rounded-lg border border-[#1F2937] divide-y divide-[#1F2937] text-xs">
                {/* Customer */}
                <div className="p-3 flex items-start gap-3">
                    <Store size={15} className="text-slate-500 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                        <p className="text-slate-500">Customer{m.customer ? <> · <span className="text-slate-300">{m.customer}</span></> : ""}</p>
                        {cust.match ? (
                            <>
                                <p className="text-slate-200 font-semibold mt-0.5">{cust.match.accountName}{cust.how === "alias" && <span className="ml-1.5 text-[10px] font-normal text-slate-500">(linked before)</span>}
                                    {cust.how === "prefix" && <span className="ml-1.5 text-[10px] font-normal text-slate-500">(name starts with this account)</span>}</p>
                                {cust.match.email
                                    ? <a href={`mailto:${cust.match.email}`} className="text-sky-300 hover:underline break-all">{cust.match.email}</a>
                                    : <p className="text-amber-300">No email on file — add it in Customer Accounts</p>}
                                {cust.match.sharedWith.length > 0 && <p className="text-slate-500 mt-0.5">Shared inbox with {cust.match.sharedWith.join(", ")}</p>}
                            </>
                        ) : cust.suggestions.length > 0 ? (
                            <div className="mt-1 space-y-1">
                                <p className="text-amber-300">Not an exact match in the contact list. Is it one of these?</p>
                                {cust.suggestions.map(s => (
                                    <p key={s.contact.id} className="flex flex-wrap items-center gap-2">
                                        <span className="text-slate-200">{s.contact.accountName}</span>
                                        <span className="text-slate-500">{s.contact.email || "no email"}</span>
                                        <button disabled={linking} onClick={() => confirmMatch(s.contact)} className="flex items-center gap-1 px-2 py-0.5 rounded border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 disabled:opacity-50">
                                            <Link2 size={11} /> Yes, link it
                                        </button>
                                    </p>
                                ))}
                                {info.linkError && <p className="text-red-400">{info.linkError}</p>}
                            </div>
                        ) : (
                            <p className="text-slate-400 mt-0.5">No contact on file</p>
                        )}
                    </div>
                    {cust.match?.email && <button onClick={() => setDialog({ type: "customer", id: cust.match.id })} className="shrink-0 flex items-center gap-1 px-2 py-1 rounded border border-[#334155] text-slate-300 hover:text-white"><Mail size={12} /> Message</button>}
                </div>
                {/* Carrier */}
                <div className="p-3 flex items-start gap-3">
                    <Truck size={15} className="text-slate-500 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                        <p className="text-slate-500">Carrier{m.carrier ? <> · <span className="text-slate-300">{m.carrier}</span></> : ""}</p>
                        {carrier ? (
                            <>
                                <p className="text-slate-200 font-semibold mt-0.5">{carrier.label}{carrier.name ? ` · ${carrier.name}` : ""}</p>
                                <a href={`mailto:${carrier.email}`} className="text-sky-300 hover:underline break-all">{carrier.email}</a>
                            </>
                        ) : (
                            <p className="text-slate-400 mt-0.5">{m.carrier ? "Carrier to confirm — no contact for it yet" : "No carrier on the reports yet"}</p>
                        )}
                    </div>
                    {carrier && <button onClick={() => setDialog({ type: "carrier", id: carrier.id })} className="shrink-0 flex items-center gap-1 px-2 py-1 rounded border border-[#334155] text-slate-300 hover:text-white"><Mail size={12} /> Message</button>}
                </div>
                {/* Sent so far */}
                {messages?.length > 0 && (
                    <div className="p-3">
                        <p className="text-slate-500 flex items-center gap-1.5 mb-1.5"><History size={12} /> Messages sent ({messages.length})</p>
                        <ul className="space-y-1 max-h-32 overflow-auto">
                            {messages.map(x => (
                                <li key={x.id} className="text-slate-400">
                                    <span className="text-slate-200">{x.subject}</span> → {x.to?.label || x.to?.name} <span className="text-slate-500">({x.to?.email}) · {x.sentBy?.name} · {fmtDateTime(x.at)}{x.dryRun ? " · test, not sent" : ""}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
            {dialog && <MessageDialog facts={facts} info={info} initial={dialog} onClose={() => setDialog(null)} onSent={() => { setDialog(null); load(); }} />}
        </div>
    );
}

function MessageDialog({ facts, info, initial, onClose, onSent }) {
    const [staff, setStaff] = useState([]);
    const [to, setTo] = useState(initial);                  // { type, id }
    const [template, setTemplate] = useState(TEMPLATE_FOR[initial.type]);
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [dirty, setDirty] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");

    const [sender, setSender] = useState(null);          // { from, replyTo, name } — one sender for every email
    useEffect(() => {
        api.get("/api/hasbro/staff").then(r => setStaff(r.data.staff || [])).catch(() => setStaff([]));
        api.get("/api/hasbro/mail-sender").then(r => setSender(r.data.sender)).catch(() => setSender(null));
    }, []);
    const signer = sender?.name || myName();

    const me = auth.currentUser?.email || "";
    const options = useMemo(() => {
        const list = [];
        const c = info.customer.match;
        if (c?.email) list.push({ type: "customer", id: c.id, name: c.accountName, email: c.email, label: "Customer" });
        info.carriers.forEach(k => list.push({ type: "carrier", id: k.id, name: k.name || k.label, email: k.email, label: k.id === info.carrier.contact?.id ? `${k.label} — this shipment's carrier` : k.label }));
        staff.filter(s => s.email !== me).forEach(s => list.push({ type: "staff", id: s.uid, name: s.name, email: s.email, label: "Hasbro colleague" }));
        return list;
    }, [info, staff, me]);
    const recipient = options.find(o => o.type === to.type && o.id === to.id) || null;

    // Fill the template in; once the user edits the text, only a template change replaces it
    useEffect(() => {
        if (dirty) return;
        const t = TEMPLATES[template];
        setSubject(t.subject(facts));
        setBody(t.body(facts, recipient, signer));
    }, [template, recipient, facts, dirty, signer]);

    const pickRecipient = value => {
        const [type, id] = value.split("|");
        setTo({ type, id });
        if (TEMPLATES[template].for !== type) { setTemplate(TEMPLATE_FOR[type]); setDirty(false); }
    };
    const pickTemplate = t => { setTemplate(t); setDirty(false); };

    const send = async () => {
        if (!recipient) return setError("Choose who to send it to.");
        if (!window.confirm(`Send this email to ${recipient.name} (${recipient.email})?`)) return;
        setSending(true);
        setError("");
        try {
            await api.post("/api/hasbro/shipment-messages", {
                shipmentKey: facts.key, recipientType: recipient.type, recipientId: recipient.id, template, subject, body,
                shipment: { orderReference: facts.orderReference, consignment: facts.consignment, po: facts.po, customer: facts.customer, carrier: facts.carrier, status: facts.status, issue: facts.issue },
            });
            onSent();
        } catch (e) {
            setError(e.response?.data?.error || "Couldn't send the message");
        } finally {
            setSending(false);
        }
    };

    const templatesFor = Object.entries(TEMPLATES).filter(([, t]) => !recipient || t.for === recipient.type);
    return (
        <div className="fixed inset-0 z-[1000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div className="w-full max-w-2xl max-h-[92vh] overflow-auto bg-[#111827] border border-[#1F2937] rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-[#1F2937]">
                    <div>
                        <p className="text-base font-bold text-white">Contact about shipment {facts.orderReference || facts.consignment}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{[facts.customer, facts.carrier, facts.status].filter(Boolean).join(" · ")}</p>
                    </div>
                    <button onClick={onClose} className="text-slate-500 hover:text-white"><X size={18} /></button>
                </div>
                <div className="px-5 py-4 space-y-4">
                    {facts.issue && <p className="text-xs text-amber-300 flex items-center gap-1.5"><AlertTriangle size={13} /> {facts.issue}</p>}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <label className="block">
                            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">To</span>
                            <select value={recipient ? `${recipient.type}|${recipient.id}` : ""} onChange={e => pickRecipient(e.target.value)} className={`${inputDark} mt-1`}>
                                {!recipient && <option value="">Choose…</option>}
                                {["customer", "carrier", "staff"].map(type => {
                                    const group = options.filter(o => o.type === type);
                                    if (!group.length) return null;
                                    return (
                                        <optgroup key={type} label={{ customer: "Customer", carrier: "Carrier", staff: "Hasbro colleague" }[type]}>
                                            {group.map(o => <option key={o.id} value={`${o.type}|${o.id}`}>{o.type === "carrier" ? o.label : o.name} — {o.email}</option>)}
                                        </optgroup>
                                    );
                                })}
                            </select>
                            {!info.customer.match?.email && <span className="block text-[11px] text-slate-500 mt-1"><Users size={11} className="inline mr-1" />Customer: no contact on file</span>}
                        </label>
                        <label className="block">
                            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Message</span>
                            <select value={template} onChange={e => pickTemplate(e.target.value)} className={`${inputDark} mt-1`}>
                                {templatesFor.map(([id, t]) => <option key={id} value={id}>{t.label}</option>)}
                            </select>
                        </label>
                    </div>
                    <label className="block">
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Subject</span>
                        <input value={subject} onChange={e => { setSubject(e.target.value); setDirty(true); }} maxLength={200} className={`${inputDark} mt-1`} />
                    </label>
                    <label className="block">
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Text</span>
                        <textarea value={body} onChange={e => { setBody(e.target.value); setDirty(true); }} rows={9} maxLength={5000} className={`${inputDark} mt-1 resize-y leading-relaxed`} />
                    </label>
                    <div className="rounded-lg bg-[#0D1520] border border-[#1F2937] px-3 py-2 text-[11px] text-slate-400 space-y-0.5">
                        <p><span className="text-slate-500">From</span> {sender ? sender.from.replace(/"/g, "") : "loading…"}</p>
                        <p><span className="text-slate-500">Replies and a copy go to</span> {sender?.replyTo || "—"}</p>
                        <p><span className="text-slate-500">Shipment details added below the text:</span> {[facts.orderReference && `shipment ${facts.orderReference}`, facts.consignment && `consignment ${facts.consignment}`, facts.po && `PO ${facts.po}`].filter(Boolean).join(", ") || "—"}</p>
                    </div>
                    {error && <p className="text-sm text-red-400">{error}</p>}
                </div>
                <div className="flex justify-end gap-2 px-5 py-4 border-t border-[#1F2937]">
                    <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-slate-300 hover:text-white">Cancel</button>
                    <button onClick={send} disabled={sending || !recipient} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-sm font-semibold text-white">
                        {sending ? <Loader size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} {sending ? "Sending…" : "Send email"}
                    </button>
                </div>
            </div>
        </div>
    );
}
